import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import type { Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import type { VisibilityOptions } from './visibility.types';

/**
 * Fire element visibility changes for user.
 *
 * ### (seen)
 * Fire once when element is visible in user's viewport.
 * # Example:
 * <div flVisibility (seen)="handleSeen()">...</div>
 */
@Directive({
  selector: `[flVisibility]`,
  standalone: true,
})
export class VisibilityDirective implements AfterViewInit, OnDestroy {
  @Output() seen = new EventEmitter();

  private options: VisibilityOptions = {
    threshold: 1.0, // how much of an object should be visible to fire seen
    debounce: 1000, // how many ms to wait before firing seen
    margin: '0px', // root element bounding box margins
  };

  private observer: IntersectionObserver;
  private intersectSubscription?: Subscription;

  private _isIntersectingSubject$ = new BehaviorSubject(false);

  constructor(
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: string,
    private timeUtils: TimeUtils,
  ) {}

  ngAfterViewInit(): void {
    // don't do on the server
    if (isPlatformBrowser(this.platformId)) {
      const config = {
        rootMargin: this.options.margin,
        threshold: this.options.threshold,
      };

      this.observer = new IntersectionObserver(
        entries =>
          this._isIntersectingSubject$.next(
            entries.find(e => e.isIntersecting) !== undefined,
          ),
        config,
      );
      this.observer.observe(this.el.nativeElement);
    }

    this.intersectSubscription = this._isIntersectingSubject$
      .pipe(
        distinctUntilChanged(),
        this.timeUtils.rxDebounceTime(1000),
        filter(isIntersecting => isIntersecting),
        take(1),
      )
      .subscribe(() => {
        this.clean();
        return this.seen.emit();
      });
  }

  clean(): void {
    this.observer?.disconnect();
  }

  ngOnDestroy(): void {
    this.clean();
    this.intersectSubscription?.unsubscribe();
  }
}
